
import { defineComponent, getCurrentInstance, PropType, ref, toRef } from "vue";
import { BaseView } from "@components/base/view";
import CiSuggestedExternalContent from "@components/ads/suggested-external-content.vue";
import UserProfileAdvisoryServices from "@components/users/profile/advisory-services.vue";
import UserProfileArticles from "@components/users/profile/articles.vue";
import UserProfileCompany from "@components/users/profile/company.vue";
import UserProfileContactModal from "@components/users/profile/contact-modal.vue";
import UserProfileDescription from "@components/users/profile/description.vue";
import UserProfileEmptyState from "@components/users/profile/empty-state.vue";
import UserProfileImprint from "@components/users/profile/imprint.vue";
import UserProfileInterestTags from "@components/users/profile/interest-tags.vue";
import UserProfilePortfolios from "@components/users/profile/portfolios.vue";
import UserProfileQualifications from "@components/users/profile/qualifications.vue";
import UserProfileProducts from "@components/users/profile/products.vue";
import UserProfileResume from "@components/users/profile/resume.vue";
import UserProfileVanguardBadge from "@components/users/profile/vanguard-badge.vue";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import { ciTrackingService } from "@services/ci-tracking.service";
import { viewportService } from "@services/viewport.service";
import UserProfileHeader from "./header.vue";
import { useUserProfile } from "./userProfileComposable";

export default defineComponent({
  name: "CiUserProfile",
  components: {
    "ci-direct-message-modal": () => import("@components/messaging/direct-message.vue"),
    "ci-suggested-external-content": CiSuggestedExternalContent,
    "ci-user-profile-advisory-services": UserProfileAdvisoryServices,
    "ci-user-profile-articles": UserProfileArticles,
    "ci-user-profile-company": UserProfileCompany,
    "ci-user-profile-contact-modal": UserProfileContactModal,
    "ci-user-profile-description": UserProfileDescription,
    "ci-user-profile-empty-state": UserProfileEmptyState,
    "ci-user-profile-header": UserProfileHeader,
    "ci-user-profile-imprint": UserProfileImprint,
    "ci-user-profile-interest-tags": UserProfileInterestTags,
    "ci-user-profile-portfolios": UserProfilePortfolios,
    "ci-user-profile-products": UserProfileProducts,
    "ci-user-profile-qualifications": UserProfileQualifications,
    "ci-user-profile-resume": UserProfileResume,
    "ci-user-profile-vanguard-badge": UserProfileVanguardBadge,
  },
  extends: BaseView,
  props: {
    user: {
      type: Object as PropType<UsersV2.User>,
      default: () => ({}),
    },
  },
  setup(props) {
    const root = getCurrentInstance()?.proxy.$root;
    const tab = ref(0);
    const showFullPageImprint = ref(false);

    const { profileOwner, targetType, showPolicies, vanguardBadge, desktopViewport } = useUserProfile(
      toRef(props, "user")
    );

    const handleImprintClick = async () => {
      await ciTrackingService.trackClick("ImprintUserProfile", props.user.id.toString(), targetType.value);

      if (viewportService.check(["xs", "sm"])) {
        showFullPageImprint.value = true;
        window.scrollTo(0, 0);
      } else {
        root?.$bvModal.show("user-imprint-modal");
      }
    };

    const handleExitFullPageImprint = () => {
      showFullPageImprint.value = false;
    };

    return {
      tab,
      showFullPageImprint,
      handleImprintClick,
      handleExitFullPageImprint,
      profileOwner,
      targetType,
      showPolicies,
      vanguardBadge,
      desktopViewport,
    };
  },
  computed: {
    adIds(): { skyscraper: string; sidebar_2nd: string } {
      return {
        skyscraper: ADS_INVENTORY.user_profile_sidebar_top.dom_id,
        sidebar_2nd: ADS_INVENTORY.user_profile_sidebar_2nd.dom_id,
      };
    },
    showProfileTab(): boolean {
      return (
        this.profileOwner ||
        Boolean(this.user.description) ||
        Boolean(this.user.company.logo_url) ||
        Boolean(this.vanguardBadge?.title) ||
        this.user.resume_items.length > 0 ||
        this.user.qualifications.length > 0 ||
        (this.user.type === "advisor" && (this.user.advisory_services.length > 0 || Boolean(this.user.imprint)))
      );
    },
    showProductsTab(): boolean {
      return this.profileOwner || this.user.portfolios_count > 0 || this.user.products_count > 0;
    },
    showContentsTab(): boolean {
      return this.profileOwner || this.user.contents_count > 0;
    },
    showEmptyState(): boolean {
      return !this.showProfileTab && !this.showProductsTab && !this.showContentsTab;
    },
    twoColumnLayout(): boolean {
      return (
        this.profileOwner ||
        this.user.type === "advisor" ||
        Boolean(this.vanguardBadge) ||
        Boolean(this.user.company.logo_url)
      );
    },
  },
});
